import React, { useCallback, useEffect, useState } from 'react'
import { useIntl } from 'react-intl'
import { isEmpty, path } from 'ramda'
import { useSelector } from 'react-redux'

import { usePlace } from 'commons/usePlace'
import { mapNumToBool } from 'commons/boolean'
import { placeStatus } from 'app/constants/place'
import useCategories from 'commons/useCategories'

import PanelPages from 'app/componentV4/Panels/Pages'
import MessagespringLoading from 'app/componentV4/Loader/MessagespringLoading'

import useProfile from './useProfile'
import Onboard from './onboard/Onboard'
import QRcode from './v4/QRcode/QRcode'
import LandingPage from './v4/LandingPage'
import PlacePublic from './v4/PlacePublic'
import OpeningHours from './v4/OpeningHours'
import PlaceInfoForm from './v4/PlaceInfoForm'
import PhotoGallery from './v4/Gallery/PhotoGallery'

const ProfileV4 = () => {
  const place = usePlace()
  const category = useCategories()
  const { formatMessage } = useIntl()
  const { fetchPlaceInfo } = useProfile()
  const pendingApproval = useSelector(
    path(['place', 'info', 'pendingApproval']),
  )
  const categoryList = useSelector((state) => state.categories.categorieList)

  const [initial, setInitial] = useState({})
  const [onboard, setOnboard] = useState(false)
  const [categories, setCategories] = useState([])

  const findCat = useCallback(
    (val) => {
      return categories.find((cat) => cat.id === val)
    },
    [categories],
  )

  const handleInitial = useCallback(async () => {
    await fetchPlaceInfo.mutate()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    const isOnboard = place.status === placeStatus.WAITING_DETAILS
    if (isOnboard) setOnboard(isOnboard)
    else handleInitial()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (isEmpty(categoryList)) {
      category.categories.mutate()
    } else setCategories(category.mapGroupCategories(categoryList))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categoryList])

  useEffect(() => {
    const { data } = fetchPlaceInfo
    if (data && categories.length > 0)
      setInitial({
        ...data,
        phoneNumber: data.phoneNumber
          ? `${data.countryCode}${data.phoneNumber}`
          : '',
        category: findCat(data?.categoryId),
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchPlaceInfo.data, categories])

  return (
    <>
      <Onboard
        isOnboard={onboard}
        onClose={() => setOnboard(false)}
        onFetchProfile={() => {
          handleInitial()
        }}
      />
      <PanelPages
        title={formatMessage({
          id: 'navigation.profile.profileInfo.label',
        })}
        rightContent={
          <PlacePublic checked={mapNumToBool(initial.mobileVisibilityStatus)} />
        }
      >
        <div className="h-full w-full">
          <div
            className={`flex space-x-5  ${pendingApproval ? 'max-h-[calc(100vh-210px)]' : 'max-h-[calc(100vh-160px)]'}`}
          >
            <div className="w-2/3 bg-white border-1 border-primary-350 rounded-tl-5 rounded-tr-5">
              {isEmpty(initial) ||
              fetchPlaceInfo.isPending ||
              category?.categories?.isPending ? (
                <div className="h-full flex items-center justify-center">
                  <MessagespringLoading />
                </div>
              ) : (
                <PlaceInfoForm
                  initial={initial}
                  isOnboard={onboard}
                  categories={categories}
                  onFetch={() => fetchPlaceInfo.mutate()}
                  isFetching={fetchPlaceInfo.isPending}
                />
              )}
            </div>
            <div className="w-1/3 space-y-5 overflow-auto">
              <QRcode placeName={initial?.name} placeLogo={initial?.logo} />
              <LandingPage placeName={initial?.name} />
              <OpeningHours />
              <PhotoGallery />
            </div>
          </div>
        </div>
      </PanelPages>
    </>
  )
}

export default ProfileV4
